/**
 * Ikonene i grensesnittet.
 *
 * De var tidligere Unicode-tegn (`✉`, `⚙`, `★` …). Problemet er at flere av dem
 * finnes både som tekst og som emoji, og hvilken variant enheten velger avhenger
 * av hvilke fonter den har. På iOS ble konvolutten og tannhjulet tegnet som
 * fargeemoji mens resten forble svarte streker – samme meny, to helt ulike
 * uttrykk. SVG ser likt ut overalt.
 *
 * Tegningene arver skriftstørrelsen (1em) og fargen (currentColor), så et ikon
 * følger teksten det står ved siden av.
 */

import type { ReactNode } from 'react';

export type IkonNavn =
  | 'oversikt'
  | 'gjester'
  | 'budsjett'
  | 'oppgaver'
  | 'bordplassering'
  | 'onskeliste'
  | 'program'
  | 'abonnement'
  | 'innstillinger'
  | 'personvern'
  | 'tannhjul'
  | 'bjelle'
  | 'laas'
  | 'brukere'
  | 'statistikk'
  | 'veikart'
  | 'kommunikasjon'
  | 'leverandorer'
  | 'skjold'
  | 'hjelp'
  | 'svar'
  | 'bok'
  | 'kort'
  | 'oye'
  | 'oyeLukket';

const STIER: Record<IkonNavn, ReactNode> = {
  // Spørsmålstegn i en ring: hjelpen i båndet. Prikken tegnes som et
  // nullstrek med rund ende, så den får samme tykkelse som resten.
  hjelp: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.5a2.5 2.5 0 0 1 4.9.6c0 1.6-2.4 2-2.4 3.5" />
      <path d="M12 17.1h.01" />
    </>
  ),
  // Hake i en ring: svaret gjesten gir.
  svar: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.4 12.4 2.5 2.5 4.9-5.3" />
    </>
  ),
  // Åpen bok: brukerveiledningen.
  bok: (
    <>
      <path d="M3 5.5h6.3A2.7 2.7 0 0 1 12 8.2V20a2.2 2.2 0 0 0-2.2-1.7H3z" />
      <path d="M21 5.5h-6.3A2.7 2.7 0 0 0 12 8.2V20a2.2 2.2 0 0 1 2.2-1.7H21z" />
    </>
  ),
  // Et kartblad med en nål i. Samme strektykkelse som resten.
  leverandorer: (
    <>
      <path d="M9 3 3 5.5v15L9 18l6 3 6-2.5v-15L15 6 9 3z" />
      <path d="M9 3v15" />
      <path d="M15 6v15" />
    </>
  ),
  onskeliste: (
    <>
      <rect x="3" y="9" width="18" height="12" rx="1.5" />
      <path d="M3 13h18" />
      <path d="M12 9v12" />
      <path d="M12 9S9.5 9 8 7.5A2.1 2.1 0 0 1 11 4.6c1 .9 1 4.4 1 4.4z" />
      <path d="M12 9s2.5 0 4-1.5A2.1 2.1 0 0 0 13 4.6c-1 .9-1 4.4-1 4.4z" />
    </>
  ),
  // To personer: brukerlista i administrasjonsportalen. «gjester» er allerede
  // en konvolutt (invitasjonen), så den kunne ikke gjenbrukes her.
  brukere: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M3.5 20c0-3.2 2.5-5.3 5.5-5.3s5.5 2.1 5.5 5.3" />
      <path d="M16.5 5.2a3.4 3.4 0 0 1 0 6.4M17.5 14.9c2.1.5 3.5 2.3 3.5 5.1" />
    </>
  ),
  // Stolper som stiger.
  statistikk: (
    <>
      <path d="M4 20V5" />
      <path d="M4 20h16" />
      <rect x="7.5" y="12" width="3.4" height="5" rx="0.8" />
      <rect x="13.2" y="8" width="3.4" height="9" rx="0.8" />
    </>
  ),
  // Skjold: merket for selve portalen.
  skjold: <path d="M12 3.2 5 6v5.4c0 4.3 2.9 7.6 7 9.4 4.1-1.8 7-5.1 7-9.4V6z" />,
  // Fire ruter: den vanlige måten å tegne en oversikt på.
  oversikt: (
    <>
      <rect x="3.25" y="3.25" width="7.5" height="7.5" rx="1.6" />
      <rect x="13.25" y="3.25" width="7.5" height="7.5" rx="1.6" />
      <rect x="3.25" y="13.25" width="7.5" height="7.5" rx="1.6" />
      <rect x="13.25" y="13.25" width="7.5" height="7.5" rx="1.6" />
    </>
  ),
  gjester: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3.5 7.5 12 13l8.5-5.5" />
    </>
  ),
  // Seddel med mynt.
  budsjett: (
    <>
      <rect x="2.75" y="6" width="18.5" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.75" />
      <path d="M6 9.5v5M18 9.5v5" />
    </>
  ),
  program: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 1.8" />
    </>
  ),
  oppgaver: (
    <>
      <path d="M4 6.5h11M4 12h11M4 17.5h7" />
      <path d="M17.5 16.2 19 17.8l3-3.4" />
    </>
  ),
  // Tidslinje med to nådde milepæler og en som ligger fram i tid.
  veikart: (
    <>
      <path d="M3 12h18" />
      <circle cx="7" cy="12" r="2.2" />
      <circle cx="12" cy="12" r="2.2" />
      <circle cx="17.5" cy="12" r="2.2" />
      <path d="M7 9.8V5.5M17.5 14.2v4.3" />
    </>
  ),
  // Konvolutt med en pil ut av seg: noe som sendes, ikke noe som ligger.
  kommunikasjon: (
    <>
      <path d="M3.5 7.2a1.7 1.7 0 0 1 1.7-1.7h9.6a1.7 1.7 0 0 1 1.7 1.7v6.6a1.7 1.7 0 0 1-1.7 1.7H5.2a1.7 1.7 0 0 1-1.7-1.7z" />
      <path d="m3.9 6.6 6.1 4.3 6.1-4.3" />
      <path d="M15.5 18.5h5M18.2 16l2.3 2.5-2.3 2.5" />
    </>
  ),
  // Rundt bord med stoler rundt.
  bordplassering: (
    <>
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="4" r="1.6" />
      <circle cx="12" cy="20" r="1.6" />
      <circle cx="4" cy="12" r="1.6" />
      <circle cx="20" cy="12" r="1.6" />
    </>
  ),
  abonnement: (
    <path d="M12 3.6l2.6 5.3 5.9.85-4.25 4.15 1 5.85L12 17l-5.25 2.75 1-5.85L3.5 9.75l5.9-.85z" />
  ),
  // Menypunktet heter «Konto og personvern», så en person sier mer enn et
  // tannhjul – og et tannhjul med tynne streker blir uansett lett til en sol.
  innstillinger: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.9 20.2a7.4 7.4 0 0 1 14.2 0" />
    </>
  ),
  // Skjold: personvern handler om å beskytte opplysninger, ikke om innstillinger.
  personvern: (
    <>
      <path d="M12 3.2l7 2.6v5.4c0 4.3-2.9 7.6-7 9.6-4.1-2-7-5.3-7-9.6V5.8z" />
      <path d="M9 12.1l2.1 2.1L15.2 10" />
    </>
  ),
  // Tannhjulet er tegnet med tolv tenner rundt et hull, ikke som eiker fra
  // midten. Eiker blir til en sol i denne størrelsen – det var nettopp feilen
  // med det forrige innstillingsikonet.
  tannhjul: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 13.6a7.6 7.6 0 0 0 0-3.2l2-1.5-2-3.4-2.3 1a7.6 7.6 0 0 0-2.8-1.6L13.9 2h-3.8l-.4 2.9a7.6 7.6 0 0 0-2.8 1.6l-2.3-1-2 3.4 2 1.5a7.6 7.6 0 0 0 0 3.2l-2 1.5 2 3.4 2.3-1a7.6 7.6 0 0 0 2.8 1.6l.4 2.9h3.8l.4-2.9a7.6 7.6 0 0 0 2.8-1.6l2.3 1 2-3.4z" />
    </>
  ),
  bjelle: (
    <>
      <path d="M18 9.5a6 6 0 1 0-12 0c0 4.4-1.6 5.9-1.6 5.9h15.2S18 13.9 18 9.5" />
      <path d="M13.8 19a2 2 0 0 1-3.6 0" />
    </>
  ),
  laas: (
    <>
      <rect x="4.75" y="10.5" width="14.5" height="10" rx="2.2" />
      <path d="M8.25 10.5V7.75a3.75 3.75 0 0 1 7.5 0v2.75" />
    </>
  ),
  // Betalingskort: magnetstripen øverst og de første sifrene nede til venstre.
  // Ikke et Visa- eller Mastercard-merke – kortbetalingen tar begge, og et av
  // logoene ville sagt at den andre ikke gjelder.
  kort: (
    <>
      <rect x="2.75" y="5.25" width="18.5" height="13.5" rx="2.4" />
      <path d="M2.75 9.9h18.5" />
      <path d="M6.4 14.6h3.6" />
    </>
  ),
  // Øyet ved passordfeltene: «vis passordet». Med en strek over betyr det
  // «skjul det igjen».
  oye: (
    <>
      <path d="M2.75 12S6.2 5.75 12 5.75 21.25 12 21.25 12 17.8 18.25 12 18.25 2.75 12 2.75 12z" />
      <circle cx="12" cy="12" r="2.9" />
    </>
  ),
  oyeLukket: (
    <>
      <path d="M2.75 12S6.2 5.75 12 5.75 21.25 12 21.25 12 17.8 18.25 12 18.25 2.75 12 2.75 12z" />
      <circle cx="12" cy="12" r="2.9" />
      <path d="M4 4l16 16" />
    </>
  ),
};

/**
 * `aria-hidden` er med vilje: hvert ikon står ved siden av sin egen tekst, så en
 * skjermleser som også leste opp ikonet ville sagt alt to ganger.
 */
export function Ikon({ navn, className }: { navn: IkonNavn; className?: string }) {
  return (
    <svg
      className={className ? `ikon ${className}` : 'ikon'}
      viewBox="0 0 24 24"
      fill={navn === 'abonnement' ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {STIER[navn]}
    </svg>
  );
}
